import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from 'core/api/queryKeys';
import { ApiError } from 'core/errors/ApiError';
import { humanizeApiError, mapFieldErrors } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { SpecialistAvatarField } from 'features/career-specialists/SpecialistAvatarField';
import { SpecialistStatusBadge } from 'features/career-specialists/SpecialistStatusBadge';
import {
  invalidateSpecialistQueries,
  normalizeSpecialist,
} from 'features/career-specialists/specialists.helpers';
import { buildSpecialistFormData, operationsApi } from 'features/operations/operations.api';
import { TIMEZONES } from 'features/operations/operations.helpers';
import { Button } from 'shared/components/Button';
import { ErrorState, PageHeader, Skeleton } from 'shared/components/Feedback';
import { useToast } from 'shared/components/Toast';
import { BilingualField, SwitchField, TextField } from 'shared/forms/Fields';
import { safeHttpUrl } from 'shared/utilities/format';
import { useUnsavedChanges } from 'shared/utilities/useUnsavedChanges';

const emptyForm = {
  name_ar: '',
  name_en: '',
  title_ar: '',
  title_en: '',
  bio_ar: '',
  bio_en: '',
  languages: ['ar'],
  timezone: 'Asia/Riyadh',
  is_active: true,
  sort_order: '1',
};

export function SpecialistFormPage({ readOnly = false }) {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { t } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [dirty, setDirty] = useState(false);
  const [conflict, setConflict] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState('');
  const [existingAvatarUrl, setExistingAvatarUrl] = useState('');
  const [removeAvatar, setRemoveAvatar] = useState(false);
  const [clientAvatarError, setClientAvatarError] = useState('');
  useUnsavedChanges(dirty && !readOnly);

  const detailQuery = useQuery({
    queryKey: queryKeys.specialist(id),
    enabled: isEdit,
    queryFn: ({ signal }) => operationsApi.showSpecialist(id, { signal }),
    select: (data) => normalizeSpecialist(data),
  });

  useEffect(() => {
    const specialist = detailQuery.data;
    if (!specialist) {
      return;
    }
    setForm({
      name_ar: specialist.name_ar || '',
      name_en: specialist.name_en || '',
      title_ar: specialist.title_ar || '',
      title_en: specialist.title_en || '',
      bio_ar: specialist.bio_ar || '',
      bio_en: specialist.bio_en || '',
      languages: specialist.languages?.length ? specialist.languages : ['ar'],
      timezone: specialist.timezone || 'Asia/Riyadh',
      is_active: specialist.is_active === true,
      sort_order: String(specialist.sort_order ?? 1),
    });
    setExistingAvatarUrl(safeHttpUrl(specialist.avatar_url) || '');
    setAvatarFile(null);
    setAvatarPreviewUrl('');
    setRemoveAvatar(false);
    setClientAvatarError('');
  }, [detailQuery.data]);

  useEffect(() => () => {
    if (avatarPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(avatarPreviewUrl);
    }
  }, [avatarPreviewUrl]);

  const mutation = useMutation({
    mutationFn: (body) => (isEdit ? operationsApi.updateSpecialist(id, body) : operationsApi.createSpecialist(body)),
    onSuccess: async (data) => {
      const specialist = normalizeSpecialist(data);
      setDirty(false);
      setConflict(null);
      if (specialist?.id != null) {
        queryClient.setQueryData(queryKeys.specialist(String(specialist.id)), specialist);
      }
      await invalidateSpecialistQueries(queryClient, specialist?.id ?? id);
      toast.success(isEdit ? t('status.updated') : t('status.created'));
      navigate(paths.specialists);
    },
    onError: (error) => {
      if (error instanceof ApiError && error.isValidation()) {
        setErrors(mapFieldErrors(error.errors));
      }
      if (error instanceof ApiError && error.isConflict()) {
        setConflict(error.message);
        return;
      }
      if (error instanceof ApiError && error.status === 403) {
        toast.error(t('errors.forbidden'));
        return;
      }
      toast.error(humanizeApiError(error, t));
    },
  });

  const change = (key, value) => {
    setDirty(true);
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handlePickFile = (file, clientError) => {
    setDirty(true);
    setClientAvatarError(clientError || '');
    setAvatarFile(file);
    setRemoveAvatar(false);
    setAvatarPreviewUrl((current) => {
      if (current.startsWith('blob:')) {
        URL.revokeObjectURL(current);
      }
      return file ? URL.createObjectURL(file) : '';
    });
  };

  const handleRemoveAvatar = () => {
    setDirty(true);
    setRemoveAvatar(true);
    setAvatarFile(null);
    setAvatarPreviewUrl((current) => {
      if (current.startsWith('blob:')) {
        URL.revokeObjectURL(current);
      }
      return '';
    });
    setClientAvatarError('');
  };

  const timezoneOptions = useMemo(
    () => (form.timezone && !TIMEZONES.includes(form.timezone) ? [form.timezone, ...TIMEZONES] : TIMEZONES),
    [form.timezone],
  );

  const onSubmit = (event) => {
    event.preventDefault();
    if (readOnly || mutation.isPending) {
      return;
    }
    setErrors({});
    if (clientAvatarError) {
      return;
    }
    const body = buildSpecialistFormData({
      name_ar: form.name_ar,
      name_en: form.name_en,
      title_ar: form.title_ar,
      title_en: form.title_en,
      bio_ar: form.bio_ar || '',
      bio_en: form.bio_en || '',
      languages: form.languages,
      timezone: form.timezone,
      is_active: form.is_active,
      sort_order: Number(form.sort_order) || 1,
    }, {
      avatarFile,
      removeAvatar,
    });
    mutation.mutate(body);
  };

  if (isEdit && detailQuery.isLoading) {
    return <Skeleton rows={6} />;
  }
  if (isEdit && detailQuery.isError) {
    return <ErrorState title={humanizeApiError(detailQuery.error, t)} onRetry={detailQuery.refetch} retryLabel={t('actions.retry')} />;
  }

  const disabled = readOnly || mutation.isPending;
  const avatarError = clientAvatarError || errors.avatar?.[0] || errors.avatar_path?.[0];
  const specialistSnapshot = detailQuery.data || {
    name_ar: form.name_ar,
    name_en: form.name_en,
    avatar_url: removeAvatar ? null : (avatarPreviewUrl || existingAvatarUrl || null),
  };

  return (
    <form className="stack-form" onSubmit={onSubmit} noValidate>
      <PageHeader
        title={readOnly ? t('specialists.detail') : isEdit ? t('specialists.edit') : t('specialists.create')}
        status={isEdit ? <SpecialistStatusBadge isActive={detailQuery.data?.is_active ?? null} /> : null}
      />
      {conflict ? (
        <p className="warning-banner" role="alert">
          {conflict} {t('conflict.keepLocal')}
          <button type="button" className="btn btn-ghost" onClick={() => detailQuery.refetch()}>{t('conflict.refresh')}</button>
        </p>
      ) : null}
      <BilingualField
        id="name"
        labelAr={t('specialists.nameAr')}
        labelEn={t('specialists.nameEn')}
        valueAr={form.name_ar}
        valueEn={form.name_en}
        onChangeAr={(value) => change('name_ar', value)}
        onChangeEn={(value) => change('name_en', value)}
        errorAr={errors.name_ar?.[0]}
        errorEn={errors.name_en?.[0]}
        disabled={disabled}
      />
      <BilingualField
        id="title"
        labelAr={t('specialists.titleAr')}
        labelEn={t('specialists.titleEn')}
        valueAr={form.title_ar}
        valueEn={form.title_en}
        onChangeAr={(value) => change('title_ar', value)}
        onChangeEn={(value) => change('title_en', value)}
        errorAr={errors.title_ar?.[0]}
        errorEn={errors.title_en?.[0]}
        disabled={disabled}
      />
      <BilingualField
        id="bio"
        labelAr={t('specialists.bioAr')}
        labelEn={t('specialists.bioEn')}
        valueAr={form.bio_ar}
        valueEn={form.bio_en}
        onChangeAr={(value) => change('bio_ar', value)}
        onChangeEn={(value) => change('bio_en', value)}
        errorAr={errors.bio_ar?.[0]}
        errorEn={errors.bio_en?.[0]}
        multiline
        required={false}
        disabled={disabled}
      />
      <SpecialistAvatarField
        existingUrl={removeAvatar ? '' : existingAvatarUrl}
        file={avatarFile}
        previewUrl={avatarPreviewUrl}
        removeRequested={removeAvatar}
        onPickFile={handlePickFile}
        onRemove={handleRemoveAvatar}
        onClearPick={() => handlePickFile(null, '')}
        error={avatarError}
        disabled={disabled}
        specialistForFallback={specialistSnapshot}
      />
      <fieldset className="choice-set">
        <legend>{t('specialists.languages')}</legend>
        {['ar', 'en'].map((code) => (
          <label key={code}>
            <input
              type="checkbox"
              checked={form.languages.includes(code)}
              disabled={disabled}
              onChange={(event) => {
                const next = event.target.checked
                  ? [...form.languages, code]
                  : form.languages.filter((item) => item !== code);
                change('languages', next.length ? next : [code]);
              }}
            />
            {code}
          </label>
        ))}
      </fieldset>
      <label className="field" htmlFor="timezone">
        <span className="field-label">{t('specialists.timezone')}</span>
        <select id="timezone" className="input" value={form.timezone} disabled={disabled} onChange={(event) => change('timezone', event.target.value)}>
          {timezoneOptions.map((zone) => <option key={zone} value={zone}>{zone}</option>)}
        </select>
      </label>
      <TextField id="sort_order" label={t('specialists.sortOrder')} type="number" value={form.sort_order} onChange={(value) => change('sort_order', value)} error={errors.sort_order?.[0]} disabled={disabled} />
      <SwitchField id="active" label={t('specialists.active')} checked={form.is_active} onChange={(value) => change('is_active', value)} disabled={disabled} />
      {readOnly
        ? <Link className="btn btn-secondary" to={paths.specialists}>{t('actions.back')}</Link>
        : <Button type="submit" loading={mutation.isPending} disabled={mutation.isPending}>{t('actions.save')}</Button>}
    </form>
  );
}
